import React, { useState, useEffect } from 'react';

/**
 * PopularTopics Component
 * 
 * Displays categorized popular tax topics that users can click to populate AskSara questions.
 * Features dynamic animations, category tabs, and click tracking.
 * 
 * Props:
 * - onTopicClick: Function to populate question field when topic is clicked
 * 
 * Data Source: /api/popular-topics (MongoDB popular_topics collection)
 * Admin Management: Phase 10 (future)
 * 
 * Categories:
 * - Deductions
 * - Business Structure
 * - Retirement
 * - Tax Planning
 * 
 * Animation Features:
 * - Staggered fade-in on mount
 * - Hover lift effect with gradient shift
 * - Category tab slide animations
 * - Typewriter effect when populating question
 */

const PopularTopics = ({ onTopicClick }) => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleTopics, setVisibleTopics] = useState([]);
  const [error, setError] = useState(null);

  // Category display names
  const categoryLabels = {
    all: 'All Topics',
    deductions: 'Deductions',
    business_structure: 'Business Structure',
    retirement: 'Retirement',
    tax_planning: 'Tax Planning'
  };

  // Category icons (using emoji for now, can replace with SVG)
  const categoryIcons = {
    all: '📋',
    deductions: '💰',
    business_structure: '🏢',
    retirement: '🏦',
    tax_planning: '📊'
  };

  // Fetch topics from API
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/popular-topics?limit=12`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch popular topics');
        }
        
        const data = await response.json();
        setTopics(data.topics || []);
        setVisibleTopics(data.topics || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching popular topics:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  // Filter topics by category
  useEffect(() => {
    if (activeCategory === 'all') {
      setVisibleTopics(topics);
    } else {
      const filtered = topics.filter(topic => topic.category === activeCategory);
      setVisibleTopics(filtered);
    }
  }, [activeCategory, topics]);

  // Handle topic click
  const handleTopicClick = async (topic) => {
    // Track click for analytics
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/popular-topics/${topic.id}/track-click`, {
        method: 'POST'
      });
    } catch (err) {
      console.error('Error tracking click:', err);
    }

    // Trigger typewriter effect by calling parent function
    if (onTopicClick) {
      onTopicClick(topic.question);
    }
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="flex gap-2 mb-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-8 bg-gray-200 rounded w-24"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
        <p className="text-red-600">Failed to load popular topics. Please try again later.</p>
      </div>
    );
  }

  if (topics.length === 0) {
    return null; // Don't show component if no topics
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl border border-blue-100 p-6 mb-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-2xl">💡</span>
        <h2 className="text-xl font-semibold text-gray-900">Popular Tax Questions</h2>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['all', 'deductions', 'business_structure', 'retirement', 'tax_planning'].map((category, index) => (
          <button
            key={category}
            onClick={() => handleCategoryChange(category)}
            className={`
              px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300
              flex items-center gap-2
              ${activeCategory === category
                ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-md scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:shadow-md'
              }
            `}
            style={{
              animation: `slideIn 0.3s ease-out ${index * 0.05}s both`
            }}
          >
            <span>{categoryIcons[category]}</span>
            <span>{categoryLabels[category]}</span>
          </button>
        ))}
      </div>

      {/* Topics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {visibleTopics.slice(0, 6).map((topic, index) => (
          <button
            key={topic.id}
            onClick={() => handleTopicClick(topic)}
            className="
              bg-white rounded-lg p-4 text-left
              border border-gray-200
              hover:shadow-lg hover:-translate-y-1 hover:border-blue-300
              transition-all duration-300
              group
              relative overflow-hidden
            "
            style={{
              animation: `fadeInUp 0.4s ease-out ${index * 0.05}s both`
            }}
          >
            {/* Gradient overlay on hover */}
            <div className="
              absolute inset-0 bg-gradient-to-br from-blue-50 to-teal-50 opacity-0
              group-hover:opacity-100 transition-opacity duration-300
            "></div>

            {/* Content */}
            <div className="relative z-10">
              <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                {topic.question}
              </p>
              
              {/* Category badge */}
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors duration-300">
                  {categoryIcons[topic.category]} {categoryLabels[topic.category]}
                </span>
              </div>
            </div>

            {/* Click indicator */}
            <div className="
              absolute bottom-2 right-2 opacity-0 group-hover:opacity-100
              transition-opacity duration-300
            ">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      {/* Footer hint */}
      {visibleTopics.length > 6 && (
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            {visibleTopics.length - 6} more topics available
          </p>
        </div>
      )}
    </div>
  );
};

export default PopularTopics;
